import { useCallback } from 'react';
import { Field, FieldRenderProps, RenderableProps, FieldInputProps } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import {
  FormControl,
  FormControlProps,
  FormGroup,
  FormGroupProps,
  FormHelperText,
  FormHelperTextProps,
} from '@mui/material';

import { showErrorOnChange as showError, useFieldForErrors } from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import ChipGroup, { IChipGroupProps } from './parts/ChipGroup';

type ChipGroupFieldProps = Omit<IChipGroupProps, 'onChange' | 'value'> & {
  name: string;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  formControlProps?: Partial<FormControlProps>;
  formGroupProps?: Partial<FormGroupProps>;
  helperText?: string;
  required?: boolean;
};

type ChipGroupWrapperProps = {
  input: FieldInputProps<string[], HTMLElement>;
  chipGroupProps: ChipGroupFieldProps;
  disabled?: boolean;
};

const ChipGroupWrapper = ({ input, chipGroupProps }: ChipGroupWrapperProps) => {
  const {
    helperText: chipGroupHelperText,
    formHelperTextProps,
    formGroupProps,
    required,
    formControlProps,
    disabled,
  } = chipGroupProps;
  const field = useFieldForErrors(input.name);
  const { isEditable } = useCanEditFormFieldsContext();
  const isError = showError(field);
  const { error, submitError } = field.meta;
  const errorText = error || submitError;
  const helperText = isError ? errorText : chipGroupHelperText;
  const isDisabled = disabled || !isEditable;

  return (
    <FormControl error={isError} required={required} {...formControlProps}>
      <FormGroup {...formGroupProps} sx={{ display: 'flex', flexDirection: 'row' }}>
        <ChipGroup
          value={input.value || []}
          onChange={input.onChange}
          disabled={isDisabled}
          {...chipGroupProps}
        />
      </FormGroup>
      {!!helperText && <FormHelperText {...formHelperTextProps}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export const ChipGroupField = (props: ChipGroupFieldProps) => {
  const renderInput = useCallback<
    NonNullable<RenderableProps<FieldRenderProps<string[]>>['render']>
  >(({ input }) => <ChipGroupWrapper input={input} chipGroupProps={props} />, [props]);

  return <Field name={props.name} render={renderInput} isEqual={isEqual} type='text' />;
};
